import useServiceReveal from '../../hooks/useServiceReveal';
import React, { useEffect, useRef } from "react";
import { editorialPage } from '../../constants/serviceEditorial';
import EditorialService from './EditorialService';
import { useParams, useNavigate } from "react-router-dom";
import { serviceCard, serviceDetailData } from "../../constants/data";
import useTranslate from "../../hooks/useTranslate";
import "./ServiceDetail.scss";
import Navbar from "../../components/Navbar/MainNavbar/Navbar";
import Footer from "../../components/Footer/Footer";

const ServiceDetail = () => {
  const { serviceSlug, subServiceSlug } = useParams();
  const navigate = useNavigate();
  const { translate, language } = useTranslate();
  const isRTL = language === "fa";
  const revealRoot = useRef(null);
  useServiceReveal(revealRoot, `${serviceSlug}-${subServiceSlug}-${language}`);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [serviceSlug, subServiceSlug]);

  // تعیین slug واقعی — اگه زیرسرویس داریم از اون استفاده کن
  const actualSlug = subServiceSlug || serviceSlug;

  // پیدا کردن سرویس از serviceCard (برای آیکون)
  const serviceBasic = serviceCard.find((s) => s.slug === serviceSlug);
  // پیدا کردن دیتای جزئیات
  const detail = serviceDetailData[actualSlug];
  const page = editorialPage(actualSlug, language);
  if (page) return <EditorialService page={page} language={language} heroImage={serviceDetailData[page.parent || actualSlug]?.heroImage} />;

  // آیا این یه زیرسرویس هست؟
  const isSubService = !!detail?.parentSlug;
  // آیا این صفحه health هست (صفحه اصلی با لینک‌ها)؟
  const isHealthMain = actualSlug === "healthcare";

  if ((!serviceBasic && !isSubService) || !detail) {
    return (
      <div ref={revealRoot} className={`service-detail-page ${actualSlug === "healthcare" || detail?.parentSlug === "healthcare" ? "" : "service-editorial"} ${isRTL ? "rtl-mode" : "ltr-mode"}`}>
        <Navbar />
        <div className="service-detail-page__not-found">
          <div className="service-detail-page__not-found-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="https://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M8 8L16 16M16 8L8 16"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h2>{translate("service.notFound") || "Service Not Found"}</h2>
          <p>
            {translate("service.notFoundDesc") ??
              "The service you're looking for doesn't exist or has been moved."}
          </p>
          <button onClick={() => navigate("/services")}>
            {translate("service.backToServices") || "Back to Services"}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div ref={revealRoot} className={`service-detail-page ${actualSlug === "healthcare" || detail?.parentSlug === "healthcare" ? "" : "service-editorial"} ${isRTL ? "rtl-mode" : "ltr-mode"}`}>
      <Navbar />

      {/* ===== Hero Section ===== */}
      <div className="service-detail-page__hero">
        <div className="service-detail-page__hero-image-wrapper">
          <img
            src={detail.heroImage}
            alt={translate(detail.title) || detail.title}
            className="service-detail-page__hero-image"
          />
          <div className="service-detail-page__hero-overlay" />
        </div>

        <div className="service-detail-page__hero-content">
          <h1 className="service-detail-page__hero-title">
            {translate(detail.titleKey)}
          </h1>
          <div className="service-detail-page__hero-line" />
        </div>
      </div>

      {/* ===== Main Content ===== */}
      <div className="service-detail-page__container">
        {/* ===== Introduction ===== */}
        <div className="service-detail-page__intro">
          <p
            className="service-detail-page__intro-text"
            style={{ whiteSpace: "pre-line" }}
          >
            {translate(detail.descriptionKey) || detail.description}
          </p>

          {/* ===== متن بلند اضافی (فقط صفحه health اصلی) ===== */}
          {isHealthMain && detail.introExtended && (
            <div className="service-detail-page__intro-extended">
              {(translate(detail.introExtendedKey) || detail.introExtended)
                .split("\n")
                .map((paragraph, i) => (
                  <p key={i} className="service-detail-page__intro-text">
                    {paragraph}
                  </p>
                ))}
            </div>
          )}
        </div>

        {/* ===== Team Members (فقط صفحه health اصلی) ===== */}
        {isHealthMain && detail.teamMembers && (
          <div className="service-detail-page__team">
            {detail.teamMembers.map((member, i) => (
              <div
                key={i}
                className="service-detail-page__team-member"
                role="link"
                tabIndex={0}
                onKeyDown={e => e.key === "Enter" && member.resumeLink && navigate(member.resumeLink)}
                onClick={() => member.resumeLink && navigate(member.resumeLink)}
                style={{ cursor: member.resumeLink ? "pointer" : "default" }}
              >
                <div className="service-detail-page__team-member-image">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={translate(member.nameKey) || member.name}
                    />
                  ) : (
                    <div className="service-detail-page__team-member-placeholder">
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="https://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="8"
                          r="4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M5 20C5 16.134 8.134 13 12 13C15.866 13 19 16.134 19 20"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <h4 className="service-detail-page__team-member-name">
                  {translate(member.nameKey) || member.name}
                </h4>
                <span className="service-detail-page__team-member-role">
                  {translate(member.roleKey) || member.role}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ===== Stats Bar ===== */}
        {/* {detail.stats && detail.stats.length > 0 && (
          <div className="service-detail-page__stats">
            {detail.stats.map((stat, i) => (
              <div key={i} className="service-detail-page__stat">
                <span className="service-detail-page__stat-value">
                  {stat.value}
                </span>
                <span className="service-detail-page__stat-label">
                  {translate(stat.label) || stat.label}
                </span>
              </div>
            ))}
          </div>
        )} */}

        {isHealthMain && <p className="service-detail-page__intro-text service-scope">{translate("services.health.scope")}</p>}
        {/* ===== Sections ===== */}
        {detail.sections?.length ? (
          <div className="service-detail-page__sections">
            {detail.sections.map((section, index) => (
              <div
                key={index}
                className={`service-detail-page__section ${
                  section.isLink ? "service-detail-page__section--linkable" : ""
                }`}
                role={section.isLink ? "link" : undefined}
                tabIndex={section.isLink ? 0 : undefined}
                onKeyDown={e => e.key === "Enter" && section.isLink && navigate(`/services/healthcare/${section.slug}`)}
                onClick={() => {
                  if (section.isLink && section.slug) {
                    navigate(`/services/healthcare/${section.slug}`);
                  }
                }}
              >
                <div className="service-detail-page__section-number">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className="service-detail-page__section-content">
                  <h2 className="service-detail-page__section-title">
                    {translate(section.titleKey) || section.title}

                    {/* فلش نشانگر لینک */}
                    {section.isLink && (
                      <svg
                        className="service-detail-page__section-arrow"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="https://www.w3.org/2000/svg"
                      >
                        <path
                          d={
                            isRTL
                              ? "M19 12H5M5 12L11 18M5 12L11 6"
                              : "M5 12H19M19 12L13 18M19 12L13 6"
                          }
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </h2>
                  <div className="service-detail-page__section-divider" />
                  <p className="service-detail-page__section-text">
                    {(translate(section.contentKey) || section.content)
                      .split("\n")
                      .map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i <
                            (
                              translate(section.contentKey) || section.content
                            ).split("\n").length -
                              1 && <br />}
                        </React.Fragment>
                      ))}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* ===== CTA / Back ===== */}
        <div className="service-detail-page__footer-actions">
          <button
            className="service-detail-page__back-btn"
            onClick={() => {
              if (isSubService) {
                // برگشت به صفحه health اصلی
                navigate(`/services/${detail.parentSlug}`);
              } else {
                navigate("/services");
              }
            }}
          >
            <span>
              {isSubService
                ? translate("service.backToHealth") ||
                  "Back to Healthcare Services"
                : translate("service.backToServices") || "Back to Services"}
            </span>
          </button>

          <button
            className="service-detail-page__contact-btn"
            onClick={() => navigate("/contact-us")}
          >
            <span>{translate("service.contactUs") || "Get in Touch"}</span>
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
